import { useEffect, useRef, useState } from "react";
import { Loader2, AlertTriangle, RotateCw, Check } from "lucide-react";
import { cn } from "@lib/utils";
import { retryUpload } from "~/lib/uploads";
import { useUploadProcessingPoll } from "~/hooks/useUploadProcessingPoll";
import { useUploadQueueStore } from "~/stores/uploadQueueStore";
import type { UploadSlot } from "~/lib/uploads";
import { VideoPlayer } from "./VideoPlayer";

interface UploadVideoSlotProps {
  // The enqueue-time slot handle. The component tracks the LIVE slot itself
  // from the queue store by this id, so consumers just hold onto whatever
  // `enqueueUpload` handed back. Pre-ingestion the live slot drives which
  // region renders; once the slot carries an ingested Upload row, that row is
  // latched and the queue slot becomes expendable — the queue tidying itself
  // up (dismissal purges terminal slots) must never unmount a player that
  // already has its video.
  slot: UploadSlot;
  className?: string;
}

// How long the transient "Processing complete" badge stays up after the
// payload flips to "ready", before it fades to nothing.
const COMPLETE_BADGE_MS = 3000;

// The single fixed-aspect region every state renders into. Sharing one
// container across states is what keeps the placeholder and the player the
// same size / rounding, so the swap is a crossfade in place, not a reflow.
function SlotFrame({
  children,
  className,
  ...rest
}: {
  children: React.ReactNode;
  className?: string;
} & React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        "relative aspect-video w-full overflow-hidden rounded-xl bg-base-900",
        className
      )}
      {...rest}
    >
      {children}
    </div>
  );
}

// The pre-completion placeholder: the Tier-0 preview frame (or a neutral dark
// panel when capture failed) with a centered uploading spinner and a subtle
// progress readout. Built to LOOK like the player that replaces it — same
// aspect ratio and rounding — so nothing shifts when the swap happens.
function UploadingPlaceholder({ slot }: { slot: UploadSlot }) {
  return (
    <SlotFrame data-testid="upload-video-placeholder" data-status={slot.status}>
      {slot.previewUrl && (
        <img
          data-testid="upload-video-preview"
          src={slot.previewUrl}
          alt=""
          className="absolute inset-0 size-full object-cover opacity-60"
        />
      )}
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-base-900/50 text-white">
        <Loader2 className="size-6 animate-spin" />
        <span className="text-xs font-medium" data-testid="upload-video-progress">
          {slot.status === "finishing"
            ? "Finishing…"
            : `Uploading — ${slot.progress}%`}
        </span>
      </div>
    </SlotFrame>
  );
}

// Compact failed state, in the queue row's red language. Retry delegates to the
// manager (same path the queue widget uses); dismiss is intentionally left to
// the queue widget so this stays a pure display of the slot.
function FailedState({ slot }: { slot: UploadSlot }) {
  return (
    <SlotFrame
      data-testid="upload-video-failed"
      data-status={slot.status}
      className="bg-base-900/90"
    >
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 px-6 text-center text-white">
        <AlertTriangle className="size-6 text-error-500" />
        <span className="text-sm font-medium">
          {slot.errorMessage ?? "Upload failed"}
        </span>
        {slot.status === "error" && (
          <button
            type="button"
            data-testid="upload-video-retry"
            onClick={() => retryUpload(slot.id)}
            className="mt-1 flex items-center gap-1 rounded-md bg-white/10 px-2 py-1 text-xs text-white hover:bg-white/20"
          >
            <RotateCw className="size-3.5" />
            Retry
          </button>
        )}
      </div>
    </SlotFrame>
  );
}

// One video slot that transitions in place across its lifecycle: an uploading
// placeholder that looks like the future player, swapped — not stacked — for a
// <VideoPlayer> the instant the upload is ingested. The player owns the region
// from there, driving its own processing / still-improving / ready affordances
// off the poll payload. This is the canonical slot component lesson and media
// pages embed.
export function UploadVideoSlot({ slot, className }: UploadVideoSlotProps) {
  // Track the live slot ourselves — the prop is just the enqueue-time handle.
  // Null once the queue purges the slot (dismissal, clearFinished).
  const liveSlot = useUploadQueueStore((s) => s.slots[slot.id] ?? null);
  // The freshest slot we ever saw, for display fields (name, progress) when
  // the live slot has just been purged.
  const lastSlotRef = useRef(liveSlot ?? slot);
  if (liveSlot) lastSlotRef.current = liveSlot;
  const displaySlot = liveSlot ?? lastSlotRef.current;

  // Latch the ingested Upload row the first time it appears. From that moment
  // the upload — not the queue slot — is the source of truth for the player;
  // the queue's bookkeeping (purging terminal slots on dismissal) must not be
  // load-bearing for a video that already ingested.
  const ingested = liveSlot?.upload ?? slot.upload ?? null;
  const [latched, setLatched] = useState(ingested);
  if (ingested && ingested.id !== latched?.id) {
    // Render-phase state adjustment (not an effect) so the player mounts the
    // same frame the upload appears — no placeholder flash back in between.
    setLatched(ingested);
  }

  // A hard-deleted upload clears the latch. The delete signal is the store's
  // deletedUploadIds set (written by the manager's X-delete, or by the poll
  // seeing a 404) — it has its own channel because the latch deliberately
  // outlives the queue slot.
  const latchedDeleted = useUploadQueueStore((s) =>
    latched != null && typeof latched.id === "number"
      ? s.deletedUploadIds.has(latched.id)
      : false
  );

  // The poll only needs the upload id + seed, so it keeps refreshing to
  // ready/failed regardless of whether the queue slot still exists.
  const { upload } = useUploadProcessingPoll(latched?.id ?? null, latched);

  // A brief "Processing complete" badge the moment the payload settles to
  // ready — an unobtrusive affordance, then nothing. Latched off the ready
  // transition so it fires once, not on every ready render.
  const [showComplete, setShowComplete] = useState(false);
  const wasReadyRef = useRef(false);
  const isReady = upload?.processing_status === "ready";
  useEffect(() => {
    if (isReady && !wasReadyRef.current) {
      wasReadyRef.current = true;
      setShowComplete(true);
      const handle = setTimeout(() => setShowComplete(false), COMPLETE_BADGE_MS);
      return () => clearTimeout(handle);
    }
    if (!isReady) wasReadyRef.current = false;
  }, [isReady]);

  // Purged before ingestion (cancelled + dismissed): there is no video to
  // show, now or ever — render nothing. Same for a latched upload the user
  // hard-deleted: the video is gone from R2 and the DB, so the slot is gone
  // from the page.
  if (!liveSlot && !latched) return null;
  if (latchedDeleted) return null;

  const isFailed =
    !latched && (displaySlot.status === "error" || displaySlot.status === "cancelled");
  const video = latched ? (upload?.video ?? null) : null;

  return (
    <div
      data-testid="upload-video-slot"
      data-slot-id={displaySlot.id}
      data-status={displaySlot.status}
      className={cn("relative", className)}
    >
      {isFailed ? (
        <FailedState slot={displaySlot} />
      ) : video ? (
        <SlotFrame data-testid="upload-video-player-frame" className="bg-transparent">
          <VideoPlayer
            video={video}
            processingStatus={upload?.processing_status ?? "processing"}
            filename={displaySlot.name}
            className="absolute inset-0 size-full rounded-none"
          />
        </SlotFrame>
      ) : (
        <UploadingPlaceholder slot={displaySlot} />
      )}

      {showComplete && (
        <div
          data-testid="upload-video-complete-badge"
          className="absolute right-3 top-3 flex items-center gap-1 rounded-md bg-success-500/90 px-2 py-1 text-xs font-medium text-white"
        >
          <Check className="size-3.5" />
          Processing complete
        </div>
      )}
    </div>
  );
}
